import { createSelector, createSlice } from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';

const initialState = {
  locale: window.userLocale,
  lastName: '',
  firstName: '',
  alias: '',
  picture: '',
  tenantList: [],
  id: null,
  rights: [],
  rightsByTenant: {},
  isInit: false,
  stripe: {
    id: null,
    status: null,
  },
  timezone: null,
};

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    update(state, { payload }) {
      state.isInit = true;
      state.id = payload.id || state.id;
      state.locale = payload.locale || state.locale;
      state.alias = payload.alias || state.alias;
      state.firstName = payload.firstName || state.firstName;
      state.lastName = payload.lastName || state.lastName;
      // The user's picture URL is constant, so it won't change even if the picture changed. Force state change to
      // reload the user's picture.
      state.picture = `${payload.picture || state.picture}?no_cache=${uuidv4()}`;
      state.rights = payload.rights || state.rights;
      state.rightsByTenant = payload.rightsByTenant || state.rightsByTenant;
      if (typeof payload.tenantList === 'string') {
        state.tenantList = JSON.parse(payload.tenantList);
      } else if (typeof payload.tenantList === 'object') {
        state.tenantList = payload.tenantList;
      }
      state.stripe = payload.stripe || state.stripe;
      state.timezone = payload.timezone || state.timezone;
    },
  },
});

export const selectHasRight = (state, right) => {
  if (Array.isArray(right)) {
    return right.every((r) => state.user.rights.includes(r));
  } else {
    return state.user.rights.includes(right);
  }
};
export const selectUserTenantIds = createSelector(
  (state) => state.user.tenantList,
  (tenantList) => {
    return Object.keys(tenantList);
  },
);

export const selectUserTenantsWithRights = (neededRights) => createSelector(
  (state) => state.user.tenantList,
  (state) => state.user.rightsByTenant,
  (tenantList, rightsByTenant) => {
    const rights = typeof neededRights === 'string' ? [neededRights] : neededRights;
    const allTenants = Object.keys(tenantList);
    // If no needed rights specified, return all tenants.
    if (!rights || rights.length === 0) {
      return allTenants;
    }
    const value = allTenants.filter((tenantId) => {
      const tenantRights = rightsByTenant[tenantId] || [];
      return rights.every((right) => tenantRights.includes(right));
    });
    return value;
  },
);

export const selectTenantMapWithRights = createSelector(
  (state) => state.user.tenantList,
  (state) => state.user.rightsByTenant,
  (state, neededRights) => neededRights,
  (tenantList, rightsByTenant, neededRights) => {
    // e.g. { "tenantId1": "Tenant Name 1", "tenantId2": "Tenant Name 2" }
    const rights = typeof neededRights === 'string' ? [neededRights] : neededRights;
    const allTenants = Object.keys(tenantList);
    if (!rights || rights.length === 0) {
      return Object.entries(tenantList).reduce((acc, [tenantId, tenant]) => {
        acc[tenantId] = tenant.name;
        return acc;
      }, {});
    }
    const rightFilteredTenants = allTenants.filter((tenantId) => {
      const tenantRights = rightsByTenant[tenantId] || [];
      return rights.every((right) => tenantRights.includes(right));
    });

    return Object.entries(tenantList).reduce((acc, [tenantId, tenant]) => {
      if (rightFilteredTenants.includes(tenantId)) {
        acc[tenantId] = tenant.name;
      }
      return acc;
    }, {});
  },
);

export const {
  update: userUpdate,
} = userSlice.actions;
export default userSlice.reducer;
